import { ArrowLeft, CheckCircle2, RotateCcw } from 'lucide-react'
import { useState } from 'react'
import {
  eligibilityQuestions,
  evaluateEligibility,
  type EligibilityAnswer,
  type EligibilityAnswers,
} from '../lib/eligibility'

interface EligibilityCheckerProps {
  onClose: () => void
  onOpenPrepare: () => void
  officialUrl: string
}

const answerLabels: Array<{ value: EligibilityAnswer; label: string }> = [
  { value: 'yes', label: '예' },
  { value: 'no', label: '아니요' },
  { value: 'unknown', label: '잘 모르겠어요' },
]

export default function EligibilityChecker({ onClose, onOpenPrepare, officialUrl }: EligibilityCheckerProps) {
  const [step, setStep] = useState(0)
  const [answers, setAnswers] = useState<EligibilityAnswers>({})
  const isComplete = step >= eligibilityQuestions.length

  const selectAnswer = (answer: EligibilityAnswer) => {
    const question = eligibilityQuestions[step]
    setAnswers((current) => ({ ...current, [question.key]: answer }))
    setStep((current) => current + 1)
  }

  const restart = () => {
    setAnswers({})
    setStep(0)
  }

  if (isComplete) {
    const result = evaluateEligibility(answers)
    return (
      <section className={`eligibility-result eligibility-result--${result.kind}`} aria-live="polite">
        <CheckCircle2 aria-hidden="true" size={34} />
        <p className="eyebrow">자가진단 결과</p>
        <h2>{result.title}</h2>
        <p>{result.message}</p>
        <p className="privacy-note">입력한 답변은 서버에 전송하거나 저장하지 않았습니다.</p>
        <div className="stacked-actions">
          <button type="button" className="button button--primary" onClick={onOpenPrepare}>
            신청 준비 확인하기
          </button>
          <a className="button button--secondary" href={officialUrl} target="_blank" rel="noreferrer">
            공식 사이트에서 최종 확인
          </a>
          <button type="button" className="button button--ghost" onClick={restart}>
            <RotateCcw aria-hidden="true" size={17} />
            다시 진단하기
          </button>
        </div>
      </section>
    )
  }

  const question = eligibilityQuestions[step]
  const progress = Math.round(((step + 1) / eligibilityQuestions.length) * 100)

  return (
    <section className="eligibility-checker">
      <div className="subpage-header">
        <button type="button" className="icon-button" onClick={onClose} aria-label="자가진단 닫기">
          <ArrowLeft aria-hidden="true" />
        </button>
        <div>
          <p className="eyebrow">지원 가능성 확인</p>
          <h2>
            {step + 1} / {eligibilityQuestions.length}
          </h2>
        </div>
      </div>
      <div className="progress" role="progressbar" aria-label="자가진단 진행률" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100}>
        <span style={{ width: `${progress}%` }} />
      </div>
      <div className="question-card">
        <h3>{question.title}</h3>
        <p>{question.help}</p>
        <div className="answer-list">
          {answerLabels.map((answer) => (
            <button key={answer.value} type="button" className="answer-button" onClick={() => selectAnswer(answer.value)}>
              {answer.label}
            </button>
          ))}
        </div>
      </div>
      <p className="privacy-note">답변은 서버로 전송되지 않으며 자격을 확정하지 않습니다.</p>
    </section>
  )
}

